import { useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import SundaySchoolLayout from '../SundaySchoolLayout';
import {
  canManageDepartment,
  getSelectedDepartmentId,
  getSundaySchoolSession,
  isChurchWideRole,
  staffRoleOf,
  STAFF_ROLES,
  type SundaySchoolStaffRole as StaffRole,
} from '../sundaySchoolSession';
import ExcelBulkImport from '../common/ExcelBulkImport';

type StaffStatus = 'pending' | 'active' | 'inactive' | 'rejected';

type StaffMember = {
  id: number;
  name: string;
  email: string;
  role: StaffRole;
  status: StaffStatus;
  joined: string;
  departmentId: number | null;
  departmentName: string | null;
  classId: number | null;
  className: string | null;
  /** 서버가 정해 준 내 권한 - 승인은 바로 위 단계만, 관리는 아래 등급 전부. */
  canApprove?: boolean;
  canManage?: boolean;
};

type Department = { id: number; name: string };
type ClassRow = { id: number; name: string };

const ROLE_DESC: Record<StaffRole, string> = {
  관리자: '교회 최종 담당자 · 교회 정보, 전체담당자 가입 승인',
  전체담당자: '주일학교 전체(모든 부서) 등록·수정, 부서 관리 · 부서담당자 가입 승인',
  부서담당자: '소속 부서 전체 등록·수정, 반·교사 관리 · 교사·스텝 가입 승인',
  교사: '소속 부서 보기 + 맡은 소그룹(반)만 등록·수정 · 학부모 가입 승인(앱) · 이 웹 콘솔은 이용 불가',
  스텝: '소속 부서 보기만',
};

const STATUS_LABEL: Record<StaffStatus, string> = {
  pending: '승인 대기',
  active: '활성',
  inactive: '비활성',
  rejected: '거절됨',
};

const STATUS_BADGE: Record<StaffStatus, string> = {
  pending: 'badge-warn',
  active: 'badge-ok',
  inactive: 'badge-gray',
  rejected: 'badge-danger',
};

export default function SundaySchoolStaff() {
  const session = getSundaySchoolSession();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [showBulk, setShowBulk] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [assignableRoles, setAssignableRoles] = useState<StaffRole[]>([]);
  const manager = canManageDepartment(session);
  const churchWide = isChurchWideRole(staffRoleOf(session));

  // 역할·담당 반·상태 변경은 서버가 행마다 알려 준 권한(자기보다 아래 등급)대로.
  const canEditRow = (s: StaffMember) => !!s.canManage;

  const load = () => {
    if (!session) return;
    axios
      .get(`${MainURL}/sundayschool/staff`, {
        params: {
          authChurch: session.user.authChurch,
          departmentId: getSelectedDepartmentId(),
          userId: session.user.id,
        },
      })
      .then(({ data }) => {
        if (data.ok) {
          setStaff(data.data);
          setAssignableRoles(data.assignableRoles ?? []);
        }
      })
      .catch((err) => console.error('GET /sundayschool/staff', err));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session) return;
    axios
      .get(`${MainURL}/sundayschool/church/departments`, {
        params: { authChurch: session.user.authChurch },
      })
      .then(({ data }) => {
        if (data.ok) setDepartments(data.data.departments);
      })
      .catch((err) => console.error('GET /sundayschool/church/departments', err));
    axios
      .get(`${MainURL}/sundayschool/church`, {
        params: {
          authChurch: session.user.authChurch,
          departmentId: getSelectedDepartmentId(),
          userId: session.user.id,
        },
      })
      .then(({ data }) => {
        if (data.ok) setClasses(data.data.classes);
      })
      .catch((err) => console.error('GET /sundayschool/church', err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const patchStaff = async (id: number, body: Record<string, unknown>) => {
    if (!session) return;
    try {
      const { data } = await axios.patch(`${MainURL}/sundayschool/staff/${id}`, {
        authChurch: session.user.authChurch,
        departmentId: getSelectedDepartmentId(),
        userId: session.user.id,
        ...body,
      });
      if (data.ok) load();
      else if (data.message) alert(data.message);
    } catch (err) {
      console.error('PATCH /sundayschool/staff/:id', err);
      alert(axios.isAxiosError(err) ? (err.response?.data?.message ?? '변경하지 못했습니다.') : '변경하지 못했습니다.');
    }
  };

  // 교사를 다른 부서로 옮기면 지금 부서 목록에서는 빠진다(담당 반도 해제된다).
  const moveDepartment = (id: number, moveToDepartmentId: number) => patchStaff(id, { moveToDepartmentId });

  const pending = staff.filter((s) => s.status === 'pending');
  const approvable = pending.filter((s) => s.canApprove);

  const approve = async (id: number) => {
    if (!session) return;
    try {
      const { data } = await axios.post(`${MainURL}/sundayschool/staff/${id}/approve`, {
        authChurch: session.user.authChurch,
        departmentId: getSelectedDepartmentId(),
        userId: session.user.id,
      });
      if (data.ok) load();
      else if (data.message) alert(data.message);
    } catch (err) {
      console.error('POST /sundayschool/staff/:id/approve', err);
      alert(axios.isAxiosError(err) ? (err.response?.data?.message ?? '승인하지 못했습니다.') : '승인하지 못했습니다.');
    }
  };

  const reject = async (id: number) => {
    if (!session) return;
    try {
      const { data } = await axios.post(`${MainURL}/sundayschool/staff/${id}/reject`, {
        authChurch: session.user.authChurch,
        departmentId: getSelectedDepartmentId(),
        userId: session.user.id,
      });
      if (data.ok) load();
    } catch (err) {
      console.error('POST /sundayschool/staff/:id/reject', err);
    }
  };

  const toggleActive = (s: StaffMember) => patchStaff(s.id, { status: s.status === 'active' ? 'inactive' : 'active' });
  const setRole = (id: number, role: StaffRole) => patchStaff(id, { role });
  const setClass = (id: number, classId: string) => patchStaff(id, { classId: classId ? Number(classId) : null });

  return (
    <SundaySchoolLayout footNote="교사·운영자 계정 · 승인 · 권한 관리">
      <div className="admin-staff">
        <div className="page-head">
          <div>
            <h1>교사·운영자 계정 🧑‍🏫</h1>
            <div className="sub">
              전체 {staff.length}명 · 승인 대기 {pending.length}명
            </div>
          </div>
          {manager && (
            <div className="flex">
              <button className="btn btn-outline" onClick={() => setShowBulk((v) => !v)}>
                📥 엑셀로 일괄 등록
              </button>
            </div>
          )}
        </div>

        <div className="card card-pad mb16">
          <div className="card-title">🔑 권한 안내</div>
          <div className="s muted mb8">가입 승인은 바로 위 단계만 할 수 있어요: 관리자 → 전체담당자 → 부서담당자 → 교사·스텝 (교사의 학부모 승인은 주일학교 앱에서)</div>
          <div className="row-list">
            {STAFF_ROLES.map((r) => (
              <div className="row" key={r}>
                <span className="chip">{r}</span>
                <div className="grow s">{ROLE_DESC[r]}</div>
              </div>
            ))}
          </div>
        </div>

        {showBulk && manager && (
          <ExcelBulkImport
            title="📥 교사·운영자 엑셀 일괄 등록"
            hint={`2행부터 입력하세요. 등록된 계정은 바로 활성 상태가 되며, 비밀번호는 6자 이상입니다. 역할은 ${assignableRoles.join('/')} 중 하나(비우면 교사). 담당 반은 이 부서의 반 이름(${classes
              .map((c) => c.name)
              .join(', ')}) 중 하나이고 비워도 됩니다.`}
            templateFileName="교사_일괄등록_양식.xlsx"
            columns={[
              { key: 'name', header: '이름', example: '박은혜' },
              {
                key: 'email',
                header: '이메일',
                example: 'teacher@example.com',
              },
              { key: 'password', header: '비밀번호', example: 'abc12345' },
              { key: 'phone', header: '연락처', example: '010-1234-5678' },
              { key: 'role', header: '역할', example: '교사' },
              {
                key: 'className',
                header: '담당 반',
                example: classes[0]?.name ?? '사랑반',
              },
            ]}
            onClose={() => setShowBulk(false)}
            onSubmit={async (rows) => {
              if (!session) return null;
              try {
                const { data } = await axios.post(`${MainURL}/sundayschool/staff/bulk`, {
                  authChurch: session.user.authChurch,
                  departmentId: getSelectedDepartmentId(),
                  userId: session.user.id,
                  rows,
                });
                if (!data.ok) return null;
                load();
                return data.data.results;
              } catch (err) {
                console.error('POST /sundayschool/staff/bulk', err);
                return null;
              }
            }}
          />
        )}

        {approvable.length > 0 && (
          <div className="card card-pad mb16">
            <div className="card-title">
              ⏳ 내가 승인할 가입 신청 <span className="chip">{approvable.length}</span>
            </div>
            <div className="row-list">
              {approvable.map((s) => (
                <div className="row" key={s.id}>
                  <span className="avatar av-2">{s.name.slice(0, 1)}</span>
                  <div className="grow">
                    <div className="t">{s.name}</div>
                    <div className="s">
                      {s.email} · {s.role} 신청
                      {isChurchWideRole(s.role) ? '' : ` · ${s.departmentName ?? '-'}`}
                      {s.className ? ` · ${s.className}` : ''} · {s.joined?.slice(0, 10)}
                    </div>
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={() => reject(s.id)}>
                    거절
                  </button>
                  <button className="btn btn-sage btn-sm" onClick={() => approve(s.id)}>
                    승인
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>이름</th>
                  <th>이메일</th>
                  <th>역할</th>
                  <th>담당 반</th>
                  <th>부서</th>
                  <th>상태</th>
                  <th>가입일</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {staff.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div className="name-cell">
                        <span className="avatar av-3">{s.name.slice(0, 1)}</span>
                        {s.name}
                      </div>
                    </td>
                    <td className="muted">{s.email}</td>
                    <td>
                      {canEditRow(s) ? (
                        <select
                          className="select role-select"
                          value={s.role}
                          onChange={(e) => setRole(s.id, e.target.value as StaffRole)}
                        >
                          {assignableRoles.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="chip">{s.role}</span>
                      )}
                    </td>
                    <td>
                      {isChurchWideRole(s.role) ? (
                        <span className="muted">-</span>
                      ) : canEditRow(s) ? (
                        <select
                          className="select role-select"
                          value={s.classId ?? ''}
                          onChange={(e) => setClass(s.id, e.target.value)}
                        >
                          <option value="">없음</option>
                          {classes.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="muted">{s.className ?? '없음'}</span>
                      )}
                    </td>
                    <td>
                      {isChurchWideRole(s.role) ? (
                        <span className="muted">전체 부서</span>
                      ) : churchWide && canEditRow(s) && departments.length > 1 ? (
                        <select
                          className="select role-select"
                          value={s.departmentId ?? ''}
                          onChange={(e) => moveDepartment(s.id, Number(e.target.value))}
                        >
                          {departments.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="muted">{s.departmentName ?? '-'}</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[s.status]}`}>{STATUS_LABEL[s.status]}</span>
                    </td>
                    <td className="muted">{s.joined?.slice(0, 10)}</td>
                    <td>
                      {s.status !== 'pending' && canEditRow(s) && (
                        <button className="btn btn-outline btn-sm" onClick={() => toggleActive(s)}>
                          {s.status === 'active' ? '비활성화' : '다시 활성화'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SundaySchoolLayout>
  );
}
